import React from 'react';
import {Navigate} from 'react-router-dom';
import {useAuth} from '../contexts/PhpAuthContext';
import {useHospitalAuth} from '../contexts/HospitalAuthContext';

const ProtectedRoute = ({children, allowedRoles = []}) => {
  const {profile, loading} = useAuth();
  const {hospitalUser, hospital, loading: hospitalLoading} = useHospitalAuth();

  // Check if this is a hospital route (wrapped with HospitalRouteGuard)
  const currentPath = window.location.pathname;
  // Check for slug-based routes (/{slug}/admin/dashboard) or legacy routes (/admin/dashboard)
  const isHospitalRoute = currentPath.match(/^\/[^/]+\/(admin|doctor|clinic)\//) || 
                          currentPath.startsWith('/admin/') || 
                          currentPath.startsWith('/doctor/') || 
                          currentPath.startsWith('/clinic/');

  if (loading || (isHospitalRoute && hospitalLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500" />
      </div>
    );
  }

  // For hospital routes, check hospital auth first
  // Check both context and localStorage (in case context hasn't updated yet after login)
  const hasHospitalAuth = (hospitalUser && hospital) || 
                          (localStorage.getItem('hospital_session_token') && localStorage.getItem('hospital_data'));
  
  if (isHospitalRoute && hasHospitalAuth) {
    // Hospital user is authenticated, allow access
    // The HospitalRouteGuard will handle subscription/approval checks
    return children;
  }

  if (!profile) {
    // Check which login page to redirect to based on the route
    if (currentPath.startsWith('/patient/')) {
      return <Navigate to="/patient/login" replace />;
    }
    // Check for slug-based routes or legacy hospital routes
    if (isHospitalRoute) {
      return <Navigate to="/hospital/login" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Ensure profile.role is valid
  if (!profile.role) {
    console.error('ProtectedRoute: Profile exists but role is missing', profile);
    // Try to infer role or redirect to login
    if (profile.patient_unique_id) {
      // It's a patient but role is missing - set it
      profile.role = 'patient';
      console.log('ProtectedRoute: Inferred role as patient from patient_unique_id');
    } else {
      // Unknown role, redirect to login
      console.error('ProtectedRoute: Cannot determine role, redirecting to login');
      localStorage.removeItem('session_token');
      if (currentPath.startsWith('/patient/')) {
        return <Navigate to="/patient/login" replace />;
      }
      if (currentPath.startsWith('/admin/') || currentPath.startsWith('/doctor/') || currentPath.startsWith('/clinic/')) {
        return <Navigate to="/hospital/login" replace />;
      }
      return <Navigate to="/login" replace />;
    }
  }
  
  // Log current route and role for debugging
  console.log('ProtectedRoute: Current path:', window.location.pathname, 'User role:', profile.role, 'Allowed roles:', allowedRoles);

  // Normalize role for comparison (case-insensitive)
  const normalizedRole = profile.role?.toLowerCase()?.trim();
  const normalizedAllowedRoles = allowedRoles.map(role => role.toLowerCase().trim());

  if (allowedRoles.length > 0 && !normalizedAllowedRoles.includes(normalizedRole)) {
    console.log('ProtectedRoute: User role', profile.role, '(normalized:', normalizedRole, ') not allowed. Allowed roles:', allowedRoles, 'Current path:', window.location.pathname);
    // Redirect based on role if they land somewhere they shouldn't
    switch (normalizedRole) {
      case 'patient':
        console.log('ProtectedRoute: Redirecting patient to /patient/dashboard');
        return <Navigate to="/patient/dashboard" replace />;
      case 'doctor':
      case 'chief_doctor':
        console.log('ProtectedRoute: Redirecting doctor to /doctor/dashboard');
        return <Navigate to="/doctor/dashboard" replace />;
      case 'admin':
        console.log('ProtectedRoute: Redirecting admin to /admin/dashboard');
        return <Navigate to="/admin/dashboard" replace />;
      case 'clinic_login':
        console.log('ProtectedRoute: Redirecting clinic_login to /clinic/dashboard');
        return <Navigate to="/clinic/dashboard" replace />;
      default:
        // For patient routes, redirect to patient login instead of home
        if (currentPath.startsWith('/patient/')) {
          console.warn('ProtectedRoute: Unknown role on patient route, redirecting to patient login');
          return <Navigate to="/patient/login" replace />;
        }
        console.warn('ProtectedRoute: Unknown role, redirecting to home');
        return <Navigate to="/" replace />;
    }
  }
  
  // Additional check: If we're on a patient route but role is not patient, redirect
  const userRole = profile.role?.toLowerCase();
  const isPatient = userRole === 'patient' || profile.patient_unique_id || profile.type === 'patient';
  
  if (currentPath.startsWith('/patient/') && !isPatient) {
    console.error('ProtectedRoute: On patient route but role is', profile.role, 'type is', profile.type, '- redirecting to appropriate dashboard');
    switch (userRole) {
      case 'doctor':
      case 'chief_doctor':
        return <Navigate to="/doctor/dashboard" replace />;
      case 'admin':
      case 'clinic_admin':
        return <Navigate to="/admin/dashboard" replace />;
      default:
        // Check if it's a hospital_user type
        if (profile.type === 'hospital_user') {
          return <Navigate to="/admin/dashboard" replace />;
        }
        // If role is unknown or missing, redirect to patient login instead of home
        console.warn('ProtectedRoute: Unknown role on patient route, redirecting to patient login');
        return <Navigate to="/patient/login" replace />;
    }
  }
  
  // Additional check: If we're on a doctor route but role is patient, redirect
  if (currentPath.startsWith('/doctor/') && profile.role === 'patient') {
    console.error('ProtectedRoute: On doctor route but role is patient - redirecting to patient dashboard');
    return <Navigate to="/patient/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
